import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { appConfirm } from '../../../components/AppConfirmModal'
import { getStudentDetail } from './api/studentsApi'
import { generateStudentPassword, sendStudentPassword } from './api/studentScreensApi'

export default function StudentGeneratePasswordScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordType, setPasswordType] = useState('auto')

  useEffect(() => {
    loadStudent()
  }, [studentId])

  const loadStudent = async () => {
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch (err) {
      appToast.error('Failed to load student')
    } finally {
      setIsLoading(false)
    }
  }

  const goBack = () => navigate('/admission/students/list')

  const generatePassword = async () => {
    try {
      setIsGenerating(true)
      const res = await generateStudentPassword(studentId, passwordType)
      setGeneratedPassword(res?.password || '')
      appToast.success(res?.message || 'Password generated successfully')
    } catch (err) {
      appToast.error('Failed to generate password')
    } finally {
      setIsGenerating(false)
    }
  }

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword)
    appToast.success('Password copied to clipboard')
  }

  const sendPasswordEmail = async () => {
    try {
      setIsGenerating(true)
      const email = student?.gEmail || student?.email || ''
      const res = await sendStudentPassword(studentId, { password: generatedPassword, email })
      appToast.success(res?.message || `Password sent to ${email}`)
    } catch (err) {
      appToast.error('Failed to send password')
    } finally {
      setIsGenerating(false)
    }
  }

  const resetPassword = async () => {
    const confirmed = await appConfirm({
      title: 'Reset Password',
      message: 'Are you sure you want to reset the password? The student will need to use the new password to log in.',
      confirmText: 'Reset Password',
      variant: 'warning',
    })
    if (!confirmed) return

    try {
      setIsGenerating(true)
      const res = await generateStudentPassword(studentId, passwordType)
      setGeneratedPassword(res?.password || '')
      appToast.success('Password reset successfully')
    } catch (err) {
      appToast.error('Failed to reset password')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      {/* Header */}
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Generate Password</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            {student ? `${student.firstName} ${student.lastName}` : 'Student'} - Generate Password
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      {isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low px-6 py-12 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
          <p className="mt-3 text-sm text-on-surface-variant">Loading...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Student Info */}
          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-xs">
            <h3 className="mb-4 text-sm font-bold text-on-surface">Student Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs text-on-surface-variant">Student Code</p>
                <p className="mt-1 font-semibold text-on-surface">{student?.studentCode || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-on-surface-variant">Email</p>
                <p className="mt-1 font-semibold text-on-surface">{student?.gEmail || student?.email || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-on-surface-variant">Full Name</p>
                <p className="mt-1 font-semibold text-on-surface">
                  {student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'}
                </p>
              </div>
            </div>
          </div>

          {/* Password Type Selection */}
          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-xs">
            <h3 className="mb-4 text-sm font-bold text-on-surface">Password Type</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3 cursor-pointer hover:border-primary">
                <input
                  type="radio"
                  name="passwordType"
                  value="auto"
                  checked={passwordType === 'auto'}
                  onChange={(e) => setPasswordType(e.target.value)}
                  className="h-4 w-4 border-primary text-primary"
                />
                <div>
                  <p className="text-sm font-semibold text-on-surface">Auto-Generated Strong Password</p>
                  <p className="text-xs text-on-surface-variant">12 characters with mix of uppercase, lowercase, numbers & symbols</p>
                </div>
              </label>

              <label className="flex items-center gap-3 rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3 cursor-pointer hover:border-primary">
                <input
                  type="radio"
                  name="passwordType"
                  value="custom"
                  checked={passwordType === 'custom'}
                  onChange={(e) => setPasswordType(e.target.value)}
                  className="h-4 w-4 border-primary text-primary"
                />
                <div>
                  <p className="text-sm font-semibold text-on-surface">Custom Pattern (BG-XXXXXXXXXX)</p>
                  <p className="text-xs text-on-surface-variant">Institutional standard format with unique identifier</p>
                </div>
              </label>
            </div>
          </div>

          {/* Password Generation */}
          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-xs">
            <h3 className="mb-4 text-sm font-bold text-on-surface">Generate New Password</h3>

            {generatedPassword && (
              <div className="mb-6 rounded-lg bg-success/10 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-on-surface-variant">Generated Password:</p>
                    <p className="mt-2 font-mono text-lg font-bold text-success">
                      {showPassword ? generatedPassword : '•'.repeat(generatedPassword.length)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="rounded-lg bg-success/20 p-2 text-success hover:bg-success/30"
                    title={showPassword ? 'Hide' : 'Show'}
                  >
                    <span className="material-symbols-outlined text-base">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={generatePassword}
                disabled={isGenerating}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-on-primary hover:bg-primary/90 disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-base">
                  {isGenerating ? 'progress_activity' : 'refresh'}
                </span>
                {isGenerating ? 'Generating...' : 'Generate Password'}
              </button>

              {generatedPassword && (
                <button
                  type="button"
                  onClick={copyPassword}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-secondary-container px-6 py-2.5 text-sm font-bold text-secondary hover:bg-secondary-container/80"
                >
                  <span className="material-symbols-outlined text-base">content_copy</span>
                  Copy Password
                </button>
              )}
            </div>
          </div>

          {/* Email & Reset Options */}
          {generatedPassword && (
            <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-xs">
              <h3 className="mb-4 text-sm font-bold text-on-surface">Send Password</h3>
              <p className="mb-4 text-sm text-on-surface-variant">
                Send the generated password to the student's email address for secure login
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={sendPasswordEmail}
                  disabled={isGenerating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-on-primary hover:bg-primary/90 disabled:opacity-60"
                >
                  <span className="material-symbols-outlined text-base">mail</span>
                  {isGenerating ? 'Sending...' : 'Send via Email'}
                </button>
                <button
                  type="button"
                  onClick={resetPassword}
                  disabled={isGenerating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-error/10 px-6 py-2.5 text-sm font-bold text-error hover:bg-error/20 disabled:opacity-60"
                >
                  <span className="material-symbols-outlined text-base">restart_alt</span>
                  Reset Password
                </button>
              </div>
            </div>
          )}

          {/* Info Notice */}
          <div className="rounded-lg border-l-4 border-warning bg-warning/10 p-4">
            <div className="flex gap-3">
              <span className="material-symbols-outlined text-warning">warning</span>
              <div>
                <p className="text-sm font-semibold text-warning">Important</p>
                <p className="mt-1 text-xs text-warning/80">
                  Generated passwords are temporary. Students should change their password on first login. Keep this
                  password confidential and never share it via unencrypted channels.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
